/* eslint-disable @typescript-eslint/naming-convention */
import {baseApi} from 'core/redux/store/api';
import {API_VERSION} from 'core/helpers/env';
import {IExpressOrderResponse, IFilterFacetResponse, IGetExpressOrderResponse} from 'plugins/modules/filter/types';
import {IProductShortResponse, TGetProductsResponse} from 'modules/products/models';
import {ICatalogSEO, IProductShort} from 'modules/products/types';
import {createListingApiService, TGetListingRequest} from 'plugins/modules/listing/api/api';
import {TGetListingTransformedResponse} from 'plugins/modules/listing/types';
import {buildHeaders} from 'core/helpers/api/headers';
import {
    clearExpressOrderStoreIdCookie,
    setExpressOrderStoreIdCookie,
} from 'modules/express-order/helpers/expressOrderStoreCookie';

export interface IGetCatalogFiltersProps {
    filter?: string;
    section: string;
}

export interface IGetCatalogTransformedResponse {
    list: TGetProductsResponse['data']['items'];
    pagination: TGetProductsResponse['pagination'];
    errors?: TGetProductsResponse['errors'];
    seo?: ICatalogSEO | null;
    meta?: TGetProductsResponse['meta'] | null;
}

interface IAddExpressOrderProps {
    idVinoteki: number;
}

const API_PATHS = {
    EXPRESS_ORDER: `${API_VERSION}/express-order`,
    CATALOG_PRODUCTS: `${API_VERSION}/catalog/products`,
    CATALOG_FACET_SEARCH: (section: string) => `${API_VERSION}/catalog/${section}/facetsearch`,
    CATALOG_SECTION: (section: string) => `${API_VERSION}/catalog/${section}`,
} as const;

const createResponseWithMeta = <T>(data: T, meta: any, additionalProps?: Record<string, any>) => ({
    data,
    ok: Boolean(meta?.response?.ok),
    ...additionalProps,
});

export const productsApiService = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        addExpressOrder: builder.mutation({
            query: (data: IAddExpressOrderProps) => ({
                body: data,
                method: 'POST',
                url: API_PATHS.EXPRESS_ORDER,
            }),
            transformResponse: (response: IExpressOrderResponse, meta) => createResponseWithMeta(response.data, meta),
            invalidatesTags: ['ExpressOrder', 'FacetFilters', 'Product/Recommendations'],
            async onQueryStarted({idVinoteki}, {queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;

                    if (data?.ok) {
                        setExpressOrderStoreIdCookie(idVinoteki);
                    }
                } catch {}
            },
        }),

        deleteExpressOrder: builder.mutation<{ok: boolean}, void>({
            query: () => ({
                method: 'DELETE',
                url: API_PATHS.EXPRESS_ORDER,
            }),
            transformResponse: (_, meta) => ({
                ok: Boolean(meta?.response?.ok),
            }),
            invalidatesTags: ['ExpressOrder', 'FacetFilters', 'Product/Recommendations'],
            async onQueryStarted(_arg, {queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;

                    if (data?.ok) {
                        clearExpressOrderStoreIdCookie();
                    }
                } catch {}
            },
        }),

        getExpressOrder: builder.query<IGetExpressOrderResponse, void>({
            query: () => ({url: API_PATHS.EXPRESS_ORDER}),
            transformResponse: (response: IGetExpressOrderResponse, meta) => ({
                data: response.data,
                isHidden: response.isHidden || !meta?.response?.ok,
            }),
            providesTags: ['ExpressOrder'],
        }),

        getFilters: builder.query({
            query: ({filter, section}: IGetCatalogFiltersProps) => {
                const baseUrl = API_PATHS.CATALOG_FACET_SEARCH(section);
                return filter ? `${baseUrl}?${filter}` : baseUrl;
            },
            providesTags: ['FacetFilters'],
            transformResponse: (response: {data: IFilterFacetResponse}) => response?.data,
        }),

        getProducts: builder.query({
            query: ({filter, pageNumber, pageSize}) => {
                const urlSP = new URLSearchParams();

                if (pageNumber) {
                    urlSP.append('pageNumber', pageNumber);
                }

                if (pageSize) {
                    urlSP.append('pageSize', pageSize);
                }

                const commonQueryString = urlSP.toString();
                let resultQueryString: string;

                if (!filter) {
                    resultQueryString = commonQueryString;
                } else {
                    resultQueryString = commonQueryString ? `${commonQueryString}&${filter}` : filter;
                }

                const query = resultQueryString ? `?${resultQueryString}` : '';

                return {url: `${API_PATHS.CATALOG_PRODUCTS}${query}`};
            },
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return `${endpointName}${queryArgs.filter}`;
            },
            transformResponse: (response: {data: IProductShortResponse}) => response?.data,
        }),
    }),
});

const productsListingApiService = createListingApiService<
    IProductShort,
    TGetProductsResponse,
    TGetListingTransformedResponse<IProductShort>,
    TGetListingRequest<{section: string}>
>({
    name: 'getCatalog',
    query: (query) => {
        const headers = buildHeaders({withSeo: 'list'});
        const sort = query.sort ? `&sort=${query.sort}` : '';

        return {
            url: `${API_PATHS.CATALOG_SECTION(query.section)}?${query.filter}&pageNumber=${query.pageNumber}&pageSize=${
                query.pageSize
            }${sort}&withMeta=${query.withMeta ? 1 : 0}`,
            headers,
        };
    },
    serializeQueryArgs: ({endpointName, queryArgs}) => {
        const storeId = queryArgs.expressOrderStoreId ?? '';
        return `${endpointName}_${queryArgs.section}${queryArgs.filter ? `_${queryArgs.filter}` : ''}_${queryArgs.sort}${storeId ? `_${storeId}` : ''}`;
    },
    forceRefetchValuesKeys: ['section', 'expressOrderStoreId'],
});

export const {getCatalog} = productsListingApiService.endpoints;
export const {useLazyGetCatalogQuery} = productsListingApiService;

export const {getProducts} = productsApiService.endpoints;
export const {
    useAddExpressOrderMutation,
    useDeleteExpressOrderMutation,
    useGetExpressOrderQuery,
    useGetFiltersQuery,
    util: {getRunningQueriesThunk},
} = productsApiService;
